import { assign } from "./common";

/**
 * Plain object extendable through a static method. Obtained subclasses are also extendable.
 *
 * Usage:
 * ```js
 * const A = ExtendableObject.extend({
 *   constructor() { this.foo = "bar"; },
 *   talk() { alert(this.foo); }
 * });
 *
 * const B = A.extend({
 *   talk() { alert(this.foo + " from a B"); }
 * });
 *
 * (new B()).talk() // -> bar from a B
 * ```
 *
 * @constructor
 */
function ExtendableObject() {}

export default ExtendableObject;

function makeExtendable(SuperClass) {

  /**
   * Returns subclass with provided prototype
   * @function extend
   * @param {object} proto                   - prototype of the subclass
   * @param {function} [proto.constructor]   - if provided, is used instead of the default constructor:
   * ```js
   * function() {
   *   SuperClass.apply(this, arguments);
   * }
   * ```
   * @memberOf ExtendableObject
   * @returns {function} SubClass
   */
  SuperClass.extend = function(proto) {
    const SubClass = proto.hasOwnProperty("constructor")
      ? function SubClass() { proto.constructor.apply(this, arguments); }
      : function SubClass() { SuperClass.apply(this, arguments); };

    SubClass.prototype = Object.create(SuperClass.prototype);
    assign(SubClass.prototype, proto);
    SubClass.prototype.constructor = SubClass;

    makeExtendable(SubClass);

    return SubClass;
  }
}

makeExtendable(ExtendableObject);
